import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';
import {
  mergeStructuredJsonModel,
  parseStructuredJsonPayload,
  serializeStructuredJsonPayload
} from '../../shared/design-system/structured-json-form.utils';
import {
  emptyStructuredJsonItem,
  parseStructuredJsonArray,
  serializeStructuredJsonArray,
  structuredJsonItemFromPayload
} from '../../shared/design-system/structured-json-form.utils';

export const REGULATORY_PAYLOAD_FIELDS: StructuredJsonFieldDef[] = [
  {
    key: 'definitionCode',
    type: 'string',
    labelKey: 'insurance.regulatory.schemas.payload.definitionCode'
  },
  {
    key: 'jurisdiction',
    type: 'select',
    labelKey: 'insurance.regulatory.schemas.payload.jurisdiction',
    options: [
      { value: 'IR', labelKey: 'insurance.regulatory.schemas.payload.jurisdictions.ir' },
      { value: 'EU', labelKey: 'insurance.regulatory.schemas.payload.jurisdictions.eu' },
      { value: 'Custom', labelKey: 'insurance.regulatory.schemas.payload.jurisdictions.custom' }
    ]
  },
  {
    key: 'schemaVersion',
    type: 'string',
    labelKey: 'insurance.regulatory.schemas.payload.schemaVersion'
  },
  {
    key: 'plate',
    type: 'string',
    labelKey: 'insurance.regulatory.schemas.payload.plate'
  },
  {
    key: 'nationalId',
    type: 'string',
    labelKey: 'insurance.regulatory.schemas.payload.nationalId'
  }
];

export const REGULATORY_VALIDATION_RESULT_SCALAR_FIELDS: StructuredJsonFieldDef[] = [
  {
    key: 'isValid',
    type: 'boolean',
    labelKey: 'insurance.regulatory.schemas.validation.isValid'
  }
];

export const REGULATORY_VALIDATION_ISSUE_FIELDS: StructuredJsonFieldDef[] = [
  {
    key: 'ruleCode',
    type: 'string',
    labelKey: 'insurance.regulatory.console.fields.ruleCode',
    required: true
  },
  {
    key: 'errorCode',
    type: 'string',
    labelKey: 'insurance.regulatory.console.fields.errorCode',
    required: true
  },
  {
    key: 'message',
    type: 'textarea',
    labelKey: 'insurance.regulatory.console.fields.errorMessage',
    required: true
  }
];

export interface RegulatoryValidationIssueRow {
  ruleCode: string;
  errorCode: string;
  message: string;
}

export function flattenRegulatoryPayloadJson(json: string): string {
  const model = parseStructuredJsonPayload(json);
  const data = model['data'];
  const dataObject = data && typeof data === 'object' && !Array.isArray(data)
    ? data as Record<string, unknown>
    : {};

  return serializeStructuredJsonPayload({
    definitionCode: model['definitionCode'] ?? '',
    jurisdiction: model['jurisdiction'] ?? '',
    schemaVersion: model['schemaVersion'] ?? '',
    plate: dataObject['plate'] ?? '',
    nationalId: dataObject['nationalId'] ?? ''
  });
}

export function expandRegulatoryPayloadJson(flatJson: string): string {
  const model = parseStructuredJsonPayload(flatJson);
  const plate = typeof model['plate'] === 'string' ? model['plate'].trim() : '';
  const nationalId = typeof model['nationalId'] === 'string' ? model['nationalId'].trim() : '';
  const data: Record<string, unknown> = {};
  if (plate) {
    data['plate'] = plate;
  }
  if (nationalId) {
    data['nationalId'] = nationalId;
  }

  const payload: Record<string, unknown> = {};
  if (typeof model['definitionCode'] === 'string' && model['definitionCode'].trim()) {
    payload['definitionCode'] = model['definitionCode'].trim();
  }
  if (typeof model['jurisdiction'] === 'string' && model['jurisdiction'].trim()) {
    payload['jurisdiction'] = model['jurisdiction'].trim();
  }
  if (typeof model['schemaVersion'] === 'string' && model['schemaVersion'].trim()) {
    payload['schemaVersion'] = model['schemaVersion'].trim();
  }
  if (Object.keys(data).length > 0) {
    payload['data'] = data;
  }

  return serializeStructuredJsonPayload(payload);
}

export function parseRegulatoryValidationIssues(json: string): RegulatoryValidationIssueRow[] {
  try {
    const parsed = JSON.parse((json ?? '').trim() || '{}') as { issues?: unknown };
    if (!Array.isArray(parsed['issues'])) {
      return [];
    }

    return parsed['issues']
      .filter((item: unknown) => item && typeof item === 'object' && !Array.isArray(item))
      .map((item: unknown) => {
        const row = item as Record<string, unknown>;
        return {
          ruleCode: typeof row['ruleCode'] === 'string' ? row['ruleCode'] : '',
          errorCode: typeof row['errorCode'] === 'string' ? row['errorCode'] : '',
          message: typeof row['message'] === 'string' ? row['message'] : ''
        };
      });
  } catch {
    return [];
  }
}

export function serializeRegulatoryValidationIssues(
  isValid: boolean,
  issues: RegulatoryValidationIssueRow[]
): string {
  return JSON.stringify({ isValid, issues }, null, 2);
}

export function regulatoryValidationIssueFromPayload(payloadJson: string): Record<string, unknown> {
  return structuredJsonItemFromPayload(REGULATORY_VALIDATION_ISSUE_FIELDS, payloadJson);
}

export function emptyRegulatoryValidationIssueItem(): Record<string, unknown> {
  return emptyStructuredJsonItem(REGULATORY_VALIDATION_ISSUE_FIELDS);
}

export function parseRegulatoryValidationIssueItems(json: string): Record<string, unknown>[] {
  return parseStructuredJsonArray(json).map(item =>
    mergeStructuredJsonModel(REGULATORY_VALIDATION_ISSUE_FIELDS, item));
}

export function serializeRegulatoryValidationIssueItems(items: Record<string, unknown>[]): string {
  return serializeStructuredJsonArray(REGULATORY_VALIDATION_ISSUE_FIELDS, items);
}
